/**
 * Sortierung nach „Wieder fällig am" (`kl_followup_date`) — die EINE Implementierung
 * für beide KL-Listen: „Staffing neu" (`pages/v2/staffing-neu.tsx`) und die
 * KFML-Worklist (`pages/v2/kfml-worklist.tsx`, PV-016).
 *
 * Regel: „nicht gesetzt" zuerst, danach aufsteigend nach Datum (das am dichtesten
 * bevorstehende/überfällige zuerst). `kl_followup_date` ist ein ISO-Datum
 * (`YYYY-MM-DD`) → lexikografischer Vergleich ist gleichbedeutend mit chronologisch.
 *
 * Bewusst generisch über der kleinsten gemeinsamen Form statt an einen Item-Typ
 * gebunden: `KlStaffingBoardItem` und `KfmlWorklistItem` tragen das Datum beide unter
 * `project.kl_followup_date`, unterscheiden sich sonst aber (nur das Staffing-Board hat
 * `requests`). Eine Bindung an einen der beiden Typen würde die andere Seite zum
 * Kopieren zwingen — und genau dann driftet die Regel auseinander.
 */

/**
 * Kleinste gemeinsame Form beider KL-Listen-Items.
 *
 * `| undefined` ist explizit nötig: das Repo fährt `exactOptionalPropertyTypes`, und beide
 * Item-Typen leiten sich von `ProjectSchema.partial()` ab → `string | null | undefined`
 * (null = kein Datum gesetzt, undefined = Feld nicht selektiert).
 */
export type KlFollowupSortable = { project: { kl_followup_date?: string | null | undefined } };

/** Kopiert die Liste (kein In-Place-Sort) und sortiert nach „Wieder fällig am". */
export function sortByKlFollowupDate<T extends KlFollowupSortable>(items: readonly T[]): T[] {
  return sortStaffingItems(items, { field: 'kl_followup_date', direction: 'asc' });
}

// ---------------------------------------------------------------------------
// Umschaltbare Sortierung (#56)
//
// Der Staffing-Bereich lässt Feld und Richtung wählen. `sortByKlFollowupDate`
// bleibt als benannter Default bestehen (die KFML-Worklist-Liste sortiert weiter
// fest danach) — implementiert wird sie aber über dieselbe Funktion, damit die
// Regel „ohne Datum zuerst" nicht an zwei Stellen gepflegt wird.
// ---------------------------------------------------------------------------

export type StaffingSortField = 'kl_followup_date' | 'project_key';
export type StaffingSortDirection = 'asc' | 'desc';

export type StaffingSortable = {
  project: {
    kl_followup_date?: string | null | undefined;
    project_key?: string | null | undefined;
  };
};

/**
 * Sortiert nach Feld und Richtung. `desc` ist die exakte Umkehrung von `asc` —
 * auch für Einträge ohne Wert. Die wandern bei `asc` nach vorne (bevorstehende
 * Wiedervorlagen sollen sichtbar bleiben) und bei `desc` folgerichtig ans Ende;
 * eine Sonderregel „ohne Wert bleibt immer oben" wäre bei umgekehrter Richtung
 * nicht mehr erklärbar.
 */
export function sortStaffingItems<T extends StaffingSortable>(
  items: readonly T[],
  opts: { field: StaffingSortField; direction: StaffingSortDirection },
): T[] {
  const factor = opts.direction === 'desc' ? -1 : 1;
  return [...items].sort((a, b) => {
    const va = a.project[opts.field] ?? null;
    const vb = b.project[opts.field] ?? null;
    if (!va && !vb) return 0;
    if (!va) return -1 * factor;
    if (!vb) return 1 * factor;
    return va.localeCompare(vb) * factor;
  });
}
